import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { Review } from '../../../model/review/review.model';

/**
 * The reviews at the foot of the themes box (owner 2026-10-11, trial on the home-reviews-box branch:
 * "ayrı bir kutu gibi değil de tema kutusunun bir parçası"): projected into app-theme-showcase, so it
 * wears the shown theme's colours. The title with the average, the themes, the platforms and the downloads under it, then the bubbles
 * (app-review-card, each painted in the theme it is about) drifting upward in columns, each at its own
 * pace (owner: "yukarı kayan farklı hızlardaki sütunlar daha iyiydi"; every review whole, never
 * clamped; no split by marketplace). The pointer over the wall holds it still to read.
 */
@Component( {
  selector: 'app-review-showcase',
  templateUrl: './review-showcase.component.html',
  styleUrls: [ './review-showcase.component.scss' ],
} )
export class ReviewShowcaseComponent implements AfterViewInit, OnDestroy {
  /** every review the home collected (marketplaces + codigrate.com buyers), shuffled */
  @Input() set reviews( value: Review[] ) {
    // the bubbles wear the shown theme, as the box does (owner 2026-10-11): their own colours are left
    // out, so app-review-card falls back to the --l-* / --d-* / --accent the wall sets from the box
    this.all = ( value || [] ).map( r => ( {
      ...r,
      lightBgFrom: undefined, lightBgTo: undefined, lightInk: undefined, lightInkSoft: undefined,
      darkBgFrom: undefined, darkBgTo: undefined, darkInk: undefined, darkInkSoft: undefined,
      accent: undefined,
    } ) );
    this.rebuild();
  }

  /** how many themes there are */
  @Input() themes = 0;

  /** how many platforms the themes ship on */
  @Input() platforms = 0;

  /** every download over every theme, formatted ('549.6k'); '' until the stats answered */
  @Input() downloads = '';

  /** false until the stats call answered (the silhouettes show meanwhile) */
  @Input() loaded = false;

  @ViewChild( 'grid' ) gridRef?: ElementRef<HTMLElement>;

  all: Review[] = [];
  columns: Review[][] = [];
  skeletonColumns = [ [ 0, 1, 2 ], [ 0, 1, 2 ], [ 0, 1, 2 ] ];

  private colCount = 3;
  private colWidth = ReviewShowcaseComponent.COL_W;
  private resizeObserver?: ResizeObserver;

  /** the old wall's card width (owner 2026-10-11: "önceki halindeki genişlik"); the box takes as
   *  many columns of it as fit, centred */
  private static readonly COL_W = 360;
  private static readonly GAP = 18;

  constructor( private zone: NgZone ) {}

  ngAfterViewInit(): void {
    // measured once now (a ResizeObserver only reports on a rendered frame), then on every resize
    this.measure( this.gridRef?.nativeElement.clientWidth || 0 );
    if ( typeof ResizeObserver === 'undefined' || !this.gridRef ) {
      return;
    }
    this.resizeObserver = new ResizeObserver( entries => this.zone.run( () => this.measure( entries[ 0 ]?.contentRect.width || 0 ) ) );
    this.resizeObserver.observe( this.gridRef.nativeElement );
  }

  /** as many columns of the card width as the box has room for (one at least, six at most) */
  private measure( w: number ): void {
    if ( !w ) {
      return;
    }
    const fit = Math.floor( ( w + ReviewShowcaseComponent.GAP ) / ( ReviewShowcaseComponent.COL_W + ReviewShowcaseComponent.GAP ) );
    const count = Math.max( 1, Math.min( 6, fit ) );
    const colWidth = Math.min( w, ReviewShowcaseComponent.COL_W );
    // only a new column count rebuilds the wall (a rebuild restarts its drift)
    if ( count !== this.colCount ) {
      this.colCount = count;
      this.colWidth = colWidth;
      // after this check, so the template is not changed while Angular is checking it
      Promise.resolve().then( () => this.rebuild() );
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /** the average of the reviews that carry a rating, one decimal */
  get average(): string {
    const rated = this.all.filter( r => r.stars > 0 );
    if ( !rated.length ) {
      return '';
    }
    return ( rated.reduce( ( sum, r ) => sum + r.stars, 0 ) / rated.length ).toFixed( 1 );
  }

  /** each star's share of the average (the last one partly filled) */
  get starFill(): number[] {
    const avg = parseFloat( this.average ) || 0;
    return [ 0, 1, 2, 3, 4 ].map( i => Math.max( 0, Math.min( 1, avg - i ) ) );
  }

  /** the line under the title: themes, platforms, downloads (the ones known so far) */
  get counts(): { value: string | number; label: string }[] {
    return [
      { value: this.themes, label: 'Themes' },
      { value: this.platforms, label: 'Platforms' },
      { value: this.downloads, label: 'Downloads' },
    ].filter( c => !!c.value );
  }

  /** each column's lap in seconds: its own pace, the same speed whatever its length */
  seconds( col: Review[], ci: number ): number {
    const perCard = [ 6.5, 8, 7.2, 7.6, 6.9, 8.3 ][ ci % 6 ];
    return Math.max( 30, col.length * perCard );
  }

  /** the columns start apart, so the bubbles never line up in rows: each one already part of the way
   *  through its lap */
  start( ci: number ): number {
    return [ 0, 0.37, 0.18, 0.61, 0.29, 0.83 ][ ci % 6 ];
  }

  /** the reviews into the columns, each to the shortest column so far (by the bubble's height as
   *  its text will set it), so every column is about as long as the others */
  private rebuild(): void {
    // a short list is repeated so every column runs taller than the wall
    const list: Review[] = [];
    for ( let i = 0; this.all.length && i < Math.max( this.all.length, this.colCount * 4 ); i++ ) {
      list.push( this.all[ i % this.all.length ] );
    }
    const cols: { r: Review; at: number }[][] = Array.from( { length: this.colCount }, () => [] );
    const heights = new Array( this.colCount ).fill( 0 );
    // the longest first, so the short ones even the columns out at the end
    list.map( ( r, at ) => ( { r, at } ) )
      .sort( ( a, b ) => this.estimate( b.r ) - this.estimate( a.r ) )
      .forEach( item => {
        const i = heights.indexOf( Math.min( ...heights ) );
        cols[ i ].push( item );
        heights[ i ] += this.estimate( item.r ) + ReviewShowcaseComponent.GAP;
      } );
    // inside a column, back in the list's own (already shuffled) order, so long and short ones mix; the
    // same every time, so a rebuild never reshuffles the wall in front of the reader
    this.columns = cols.map( col => col.sort( ( a, b ) => a.at - b.at ).map( item => item.r ) );
  }

  /** a bubble's height: the header and the padding (~118px) plus the text's lines (13.5px type at
   *  1.6, ~6.9px a character, the bubble's 28px of side padding off the column) */
  private estimate( r: Review ): number {
    const perLine = Math.max( 20, Math.floor( ( this.colWidth - 28 ) / 6.9 ) );
    const lines = Math.max( 1, Math.ceil( ( r.review || '' ).length / perLine ) );
    return 118 + lines * 21.6;
  }
}
